import React, { useState, forwardRef, useRef } from 'react';
import clsx from 'clsx';
import { v4 as uuidv4 } from 'uuid';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';

import { Info, ThrashBin } from '../../../icons';

export const EquationsField = ({ values, selectedValues, onChange }) => {
  const modalRef = useRef();
  const formula = getFormulaString(selectedValues);

  return (
    <>
      <ol
        className="list-none px-2 rounded-md bg-white text-black cursor-pointer [counter-reset:with-braces]"
        onClick={() => modalRef?.current?.showModal()}
      >
        {formula.length > 0 ? (
          formula.map((li, i) => (
            <li>
              {i + 1}) &nbsp;{li}
            </li>
          ))
        ) : (
          // placeholder so that field is always visible
          <li className="text-secondary">Wprowadź równanie</li>
        )}
      </ol>
      <EquationsInputModal
        ref={modalRef}
        defaultValue={selectedValues}
        values={{
          formulas: values.formulas,
          columns: values.numerical_columns,
        }}
        onModalConfirm={(equations) => onChange(equations)}
      />
    </>
  );
};

const EquationsInputModal = forwardRef(function EquationsModal(props, ref) {
  const [equations, setEquations] = useState(
    // deepcopy
    props.defaultValue ? props.defaultValue.map((eq) => ({ ...eq })) : [],
  );
  const maxEquations = 6;

  const handleAddButton = () => {
    setEquations((p) => {
      const newEq = [...p];
      newEq.push({
        formula_x: undefined,
        formula_y: undefined,
        var_x: undefined,
        var_y: undefined,
      });
      return newEq;
    });
  };
  const handleRemoveBtn = (eqIndex) => {
    setEquations((p) => {
      const newEq = [...p];
      newEq.splice(eqIndex, 1);
      return newEq;
    });
  };
  const handleCancelBtn = (event) => {
    // reset to default
    setEquations(props.defaultValue ? props.defaultValue : []);
    ref.current?.close();
  };
  const handleModalConfirm = (event) => {
    props.onModalConfirm(equations);
    ref.current?.close();
  };

  const setEquation = (equationNumber, equationPart, value, equations) => {
    const newEq = equations.map((eq) => ({ ...eq }));
    let eq = newEq[equationNumber];
    eq[equationPart] = value;
    return newEq;
  };

  const faultyEquationIndex = equations.findIndex((eq) =>
    Object.values(eq).some((v) => v === undefined),
  );

  return (
    <dialog ref={ref} className="modal max-h-screen overflow-y-scroll">
      <div className="modal-box max-w-screen-lg [&>*]:!text-black">
        <h2 className="text-2xl font-bold mt-2">Wprowadź zmienne równania</h2>
        <div className="overflow-x-auto">
          <table className="table">
            <thead className=" border-b-0">
              <tr className="border-b-0">
                <th>Numer równania</th>
                <th>Nazwa zmiennej prognozowanej y</th>
                <th>Przekształcenie</th>
                <th>Nazwa zmiennej objaśniającej x</th>
                <th>Przekształcenie</th>
              </tr>
            </thead>
            <tbody>
              {equations &&
                equations.map((equation, index) => (
                  <tr className="border-b-0" key={uuidv4()}>
                    <th>{index + 1}</th>
                    <td>
                      <EquationElement
                        options={props.values.columns}
                        value={equation.var_y}
                        onChange={(event) =>
                          setEquations((p) =>
                            setEquation(index, 'var_y', event.target.value, p),
                          )
                        }
                        ariaLabel="Wybierz zmienną Y"
                      />
                    </td>
                    <td>
                      <EquationElement
                        options={props.values.formulas.map((f) => f.name)}
                        value={equation.formula_y}
                        onChange={(event) =>
                          setEquations((p) =>
                            setEquation(
                              index,
                              'formula_y',
                              event.target.value,
                              p,
                            ),
                          )
                        }
                        ariaLabel="Wybierz formułę dla Y"
                      />
                    </td>
                    <td>
                      <EquationElement
                        options={props.values.columns}
                        value={equation.var_x}
                        onChange={(event) =>
                          setEquations((p) =>
                            setEquation(index, 'var_x', event.target.value, p),
                          )
                        }
                        ariaLabel="Wybierz zmienną X"
                      />
                    </td>
                    <td>
                      <EquationElement
                        options={props.values.formulas.map((f) => f.name)}
                        value={equation.formula_x}
                        onChange={(event) =>
                          setEquations((p) =>
                            setEquation(
                              index,
                              'formula_x',
                              event.target.value,
                              p,
                            ),
                          )
                        }
                        ariaLabel="Wybierz formułę dla X"
                      />
                    </td>
                    <td>
                      <button
                        onClick={() => handleRemoveBtn(index)}
                        className={clsx(
                          'btn btn-circle btn-ghost p-0 text-error hover:bg-error hover:text-white',
                          equations.length == 1 &&
                            'btn-disabled cursor-not-allowed opacity-50',
                        )}
                        disabled={equations.length == 1}
                        type="button"
                        aria-label="Usuń równanie"
                      >
                        <ThrashBin />
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="collapse bg-inherit -my-3">
          <input type="checkbox" aria-labelledby="help-section-label" />
          <div
            className="collapse-title font-medium flex gap-2 items-center"
            id="help-section-label"
          >
            <Info className={'size-6'} />
            Pomoc
          </div>
          <div className="collapse-content grid grid-cols-4 text-xs gap-2">
            {props.values.formulas.map((f) => (
              <div>
                {f.name} - {f.description}
              </div>
            ))}
          </div>
        </div>
        <div className="modal-action justify-between mt-8">
          <button className="btn" onClick={handleCancelBtn} type="button">
            Anuluj
          </button>
          <div className="flex gap-6">
            <button
              onClick={handleAddButton}
              className="btn !btn-success font-bold"
              disabled={equations.length == maxEquations}
              type="button"
            >
              + Dodaj kolejne
            </button>
            {/* TODO: adjust .app_side_content_* text-black tailwind selector */}
            <Tippy
              content={
                equations.length == 0
                  ? 'Wprowadź przynajmniej jedno równanie'
                  : faultyEquationIndex > -1
                    ? `Uzupełnij w pełni równanie ${faultyEquationIndex + 1}`
                    : ''
              }
              disabled={!(equations.length == 0 || faultyEquationIndex > -1)}
              placement="top"
              theme="custom-dark"
              zIndex={9999}
              appendTo="parent"
            >
              <div>
                <button
                  className={clsx('btn btn-primary')}
                  onClick={(event) => handleModalConfirm(event)}
                  disabled={faultyEquationIndex > -1 || equations.length == 0}
                  type="button"
                >
                  Zatwierdź
                </button>
              </div>
            </Tippy>
          </div>
        </div>
      </div>
    </dialog>
  );
});

function EquationElement({ options, value, onChange, ariaLabel }) {
  return (
    <select
      className="select select-bordered w-full max-w-xs"
      name="opts"
      required
      defaultValue={value ?? ''}
      onChange={onChange}
      aria-label={ariaLabel}
    >
      <option disabled hidden value=""></option>
      {options.map((o) => (
        <option key={o}>{o}</option>
      ))}
    </select>
  );
}

const getFormulaString = (equations) =>
  equations
    ? equations.map(({ var_y, formula_y, var_x, formula_x }) => {
        let result = '';
        if (formula_y !== 'none') {
          result += `${formula_y}(${var_y})`;
        } else {
          result += var_y;
        }

        result += ' ~ ';

        if (formula_x !== 'none') {
          result += `${formula_x}(${var_x})`;
        } else {
          result += var_x;
        }
        return result;
      })
    : '';
